import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, MapPin, ArrowRight } from "lucide-react";
import { PageHeader, SectionLabel } from "@/components/shared";
import { FadeUp } from "@/components/motion/Reveal";
import { getEvents } from "@/lib/api";
import { ASSETS } from "@/lib/assets";

function fmt(dateStr) {
  const d = new Date(`${dateStr}T12:00:00`);
  return {
    month: d.toLocaleDateString("en-CA", { month: "short" }).toUpperCase(),
    day: d.toLocaleDateString("en-CA", { day: "2-digit" }),
    weekday: d.toLocaleDateString("en-CA", { weekday: "long" }),
    full: d.toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" }),
  };
}

function fmtRange(dateStr, endStr) {
  const d = new Date(`${dateStr}T12:00:00`);
  const full = { year: "numeric", month: "long", day: "numeric" };
  if (!endStr) return d.toLocaleDateString("en-CA", full);
  const e = new Date(`${endStr}T12:00:00`);
  const sameYear = d.getFullYear() === e.getFullYear();
  const sameMonth = sameYear && d.getMonth() === e.getMonth();
  if (sameMonth) return `${d.toLocaleDateString("en-CA", { month: "long" })} ${d.getDate()}\u2013${e.getDate()}, ${d.getFullYear()}`;
  if (sameYear) return `${d.toLocaleDateString("en-CA", { month: "long", day: "numeric" })} \u2013 ${e.toLocaleDateString("en-CA", { month: "long", day: "numeric" })}, ${d.getFullYear()}`;
  return `${d.toLocaleDateString("en-CA", full)} \u2013 ${e.toLocaleDateString("en-CA", full)}`;
}

export default function Events() {
  const [events, setEvents] = useState([]);
  useEffect(() => { getEvents().then(setEvents).catch(() => {}); }, []);

  return (
    <div data-testid="events-page">
      <PageHeader
        testId="events-header"
        label="Gather"
        title={<>Upcoming <span className="text-wutn">Events</span></>}
        subtitle="Gatherings, prayer retreats, training, and outreach. Come expectant — God shows up when His people gather."
        image={ASSETS.groupAdults}
      />

      <section className="bg-black py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <SectionLabel className="mb-10">The Calendar</SectionLabel>
          <div className="border-t border-white/10">
            {events.map((e, i) => {
              const d = fmt(e.date);
              const d2 = e.end_date ? fmt(e.end_date) : null;
              const sameMonth = d2 && d2.month === d.month;
              return (
                <FadeUp key={e.id} delay={i * 0.05}>
                  <div data-testid={`event-${e.id}`} className="group grid md:grid-cols-12 gap-6 items-center py-8 border-b border-white/10 hover:bg-white/[0.02] transition-colors px-2">
                    <div className="md:col-span-2 flex md:block items-baseline gap-3">
                      <div className="font-display text-5xl leading-none group-hover:text-wutn transition-colors">
                        {d.day}{d2 ? `–${d2.day}` : ""}
                      </div>
                      <div className="font-cond uppercase tracking-widest text-wutn text-sm mt-1">
                        {d.month}{d2 && !sameMonth ? ` – ${d2.month}` : ""}
                      </div>
                    </div>
                    <div className="md:col-span-6">
                      <span className="text-xs uppercase tracking-[0.2em] text-zinc-500">{e.category}</span>
                      <h3 className="mt-1 font-display uppercase text-2xl md:text-3xl leading-tight">{e.title}</h3>
                      <p className="mt-2 text-zinc-400 text-sm max-w-xl">{e.description}</p>
                    </div>
                    <div className="md:col-span-3 space-y-1.5 text-sm text-zinc-400">
                      <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-wutn" /> {fmtRange(e.date, e.end_date)}</div>
                      {e.time && <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-wutn" /> {e.time}</div>}
                      <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-wutn" /> {e.location}</div>
                    </div>
                    <div className="md:col-span-1 flex md:justify-end">
                      <Link to="/book" data-testid={`event-rsvp-${e.id}`} className="inline-flex items-center justify-center h-11 w-11 bg-transparent border border-white/20 group-hover:bg-wutn group-hover:border-wutn transition-colors">
                        <ArrowRight className="h-5 w-5" />
                      </Link>
                    </div>
                    {e.image_url && (
                      <div className="md:col-span-12">
                        <img src={e.image_url} alt={e.title} loading="lazy" className="w-full aspect-[16/9] md:aspect-[21/9] object-cover border border-white/10" data-testid={`event-photo-${e.id}`} />
                      </div>
                    )}
                  </div>
                </FadeUp>
              );
            })}
          </div>

          <div className="mt-16 bg-[#0A0A0A] border border-white/10 p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <h3 className="font-display uppercase text-3xl md:text-4xl">Want Us At Your Church?</h3>
              <p className="mt-2 text-zinc-400 max-w-xl">Invite Kerby to preach, or bring Wake-Up The Nation to your city for an awakening night.</p>
            </div>
            <Link to="/contact" data-testid="events-invite-btn" className="shrink-0 inline-flex items-center gap-2 bg-wutn hover:bg-wutn-dark text-white px-7 py-4 font-cond uppercase tracking-wider text-sm transition-colors">
              Send An Invite <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
